<template>
	<div class="pt-3">
			<!-- <h2 class="title-3 text-grey-900 w-100 pb-4">
				{{ content.station_whereIsLocated }}
			</h2> -->
		<div class="map-container d-flex justify-content-center align-items-center" :style="{
			'background-image': `url(${mapPlaceHolder})`
		}"
			style="width: 100%; height: 400px; border-radius: var(--border-radius-card-secondary)">
			<div class="open-map-container col-md-6 col-10 bg-primary-white d-flex flex-column align-items-center gap-3">
				<div class="title-3 text-grey-900 mt-1 col-12">
					{{ content['map_location_accommodation'] }} {{ lihe?.name || "" }}
				</div>
				<button @click="handleOpenMapClick" type="button" class="button-secondary bg-none border-primary-blue text-primary-blue d-flex justify-content-evenly open-map-button col-md-4 col-12">
					<div class="buttons-small text-primary-blue mt-1">
						{{ content['map_seeMap'] }}
					</div>
					<div>
						<svg style="height: 24px; width: 24px" class="icon tf-icon-16  text-blue-900"> <use xlink:href="#___icon___marker"></use> </svg>
					</div>
				</button>
			</div>
				
		</div>
		<MapComponent v-if="isMapOpen" />
	</div>
</template>

<script lang="ts" setup>
/**
 * Map Container Component (App-v2.vue)
 * 
 * This component displays a placeholder map with a button to open the full map view.
 * When the button is clicked, it conditionally renders the MapComponent which handles
 * fullscreen display (desktop) or CSS overlay (iOS Safari).
 * 
 * @component
 */

// Vue
import { ref, onMounted, watch} from "vue";
// components
import MapComponent from "@/vue/map/components/map-v2.vue";
// composables
import useMapContainerVisibility from "@/vue/composables/useMapContainerVisibility";

// Images
import mapPlaceHolder from "@/images/mapPlaceHolder.png"

/**
 * Reactive reference tracking whether the map component is currently open/visible.
 * When true, the MapComponent is conditionally rendered.
 * 
 * @type {Ref<boolean>}
 */
const isMapOpen = ref(false);

/**
 * Composable for toggling map container visibility.
 */
const { toggleMapContainerVisibility } = useMapContainerVisibility();

/**
 * Watcher for isMapOpen that toggles the d-none class on vue-map-container.
 */
watch(isMapOpen, (newValue) => {
	toggleMapContainerVisibility(newValue);
});

/**
 * Handles the click event on the "View on map" button.
 * 
 * This function toggles the map component visibility:
 * - If map is closed: Opens it and dispatches 'openMapClick' event
 *   (MapComponent will auto-trigger fullscreen/overlay on mount)
 * - If map is open: Dispatches 'openMapClick' event to close overlay/fullscreen,
 *   then closes the component (cleanup happens in MapComponent's beforeUnmount)
 * 
 * @function handleOpenMapClick
 * @returns {void}
 */
const handleOpenMapClick = (): void => {
	if (isMapOpen.value) {
		// Map is already open, close it
		// Dispatch event to trigger toggle (will close overlay/fullscreen)
		window.dispatchEvent(new Event("openMapClick"))
		// Close the component (beforeUnmount will clean up overlay)
		isMapOpen.value = false
	} else {
		// Map is closed, open it
		isMapOpen.value = true
		// The MapComponent will auto-trigger fullscreen/overlay when it mounts
		// We still dispatch the event for cases where component is already mounted
		window.dispatchEvent(new Event("openMapClick"))
	}
}

const content = window.content
const lihe = window.lihe

/**
 * Lifecycle hook: Sets up event listeners for fullscreen changes.
 * 
 * Listens to fullscreen API events from all browser vendors to detect when
 * the user exits fullscreen mode (e.g., pressing ESC). When fullscreen is
 * exited, the map component is automatically closed.
 * 
 * Supported browser prefixes:
 * - Standard: fullscreenchange
 * - WebKit (Safari, Chrome): webkitfullscreenchange
 * - Mozilla (Firefox): mozfullscreenchange
 * - Microsoft (IE/Edge): MSFullscreenChange
 * 
 * @function onMounted
 * @returns {void}
 */
onMounted(() => {
	// Initialize the visibility state based on initial isMapOpen value
	toggleMapContainerVisibility(isMapOpen.value);
	
	// Handle fullscreen changes (desktop browsers)
	document.addEventListener("fullscreenchange", () => {
		const doc = document as any;
		if (!document.fullscreenElement && 
			!doc['webkitFullscreenElement'] && 
			!doc['mozFullScreenElement'] && 
			!doc['msFullscreenElement']) {
			isMapOpen.value = false;
		}
	});
	
	// Also listen for webkit fullscreen events (Safari desktop)
	document.addEventListener("webkitfullscreenchange", () => {
		const doc = document as any;
		if (!doc['webkitFullscreenElement']) {
			isMapOpen.value = false;
		}
	});
	
	document.addEventListener("mozfullscreenchange", () => {
		const doc = document as any;
		if (!doc['mozFullScreenElement']) {
			isMapOpen.value = false;
		}
	});
	
	document.addEventListener("MSFullscreenChange", () => {
		const doc = document as any;
		if (!doc['msFullscreenElement']) {
			isMapOpen.value = false;
		}
	});
});

</script>

<style>
.vue-map-container .vue-map  {
	border-radius: var(--border-radius-card-secondary);
}

.map-container {
	background-repeat: no-repeat;
	background-position: center;
	background-size: cover; 
}
.open-map-container {
	padding: 25px 31px 25px 31px;
	border-radius: var(--border-radius-secondary);
	border: 1px;

}

.open-map-button {
	padding: 5px !important;
}
</style>
